<script setup lang="ts">
import { usePage } from '@inertiajs/vue3';
import { Link } from '@inertiajs/vue3';
import {
    AlertTriangle,
    CalendarClock,
    CreditCard,
    ShieldAlert,
} from 'lucide-vue-next';
import { computed } from 'vue';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useI18n } from '@/composables/useI18n';
import * as subscriptionsRoute from '@/routes/subscriptions';
import type { ChannelAlert, SubscriptionAlert } from '@/types';

/**
 * Alertes persistantes globales (contrairement aux toasts, elles restent
 * affichées tant que la condition est vraie) :
 * - abonnement en attente de paiement, en retard, expiré ou sur le point
 *   de se terminer ;
 * - instances WhatsApp bannies ou proches des plafonds anti-ban.
 */
const { __ } = useI18n();

const page = usePage();
const user = computed(() => page.props.auth?.user ?? null);

const subscriptionAlert = computed<SubscriptionAlert | null>(
    () =>
        (user.value?.subscription_alert as SubscriptionAlert | undefined) ??
        null,
);

const channelAlerts = computed<ChannelAlert[]>(
    () => (user.value?.channel_alerts as ChannelAlert[] | undefined) ?? [],
);

const manageUrl = subscriptionsRoute.manage().url;

const formatDate = (iso: string | null | undefined) => {
    if (!iso) {
        return null;
    }

    return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    }).format(new Date(iso));
};

const daysLabel = (days: number) =>
    __(
        days === 0
            ? 'alerts.subscription_expiring_today'
            : days === 1
              ? 'alerts.subscription_expiring_days_one'
              : 'alerts.subscription_expiring_days_many',
        { days },
    );
</script>

<template>
    <div class="space-y-3">
        <!-- Abonnement -->
        <Alert v-if="subscriptionAlert?.type === 'expiring'" variant="warning">
            <CalendarClock class="h-4 w-4" />
            <AlertTitle>
                {{ __('alerts.subscription_expiring_title') }}
            </AlertTitle>
            <AlertDescription>
                <span>
                    {{
                        __('alerts.subscription_expiring_description', {
                            plan:
                                subscriptionAlert.plan_name ??
                                __('alerts.subscription_plan'),
                            days: daysLabel(subscriptionAlert.days_left ?? 0),
                            date: formatDate(subscriptionAlert.ends_at) ?? '',
                        })
                    }}
                </span>
                <Link
                    :href="manageUrl"
                    class="ml-2 underline underline-offset-4"
                >
                    {{ __('alerts.renew_now') }}
                </Link>
            </AlertDescription>
        </Alert>

        <Alert
            v-else-if="subscriptionAlert?.type === 'past_due'"
            variant="warning"
        >
            <AlertTriangle class="h-4 w-4" />
            <AlertTitle>{{
                __('alerts.subscription_past_due_title')
            }}</AlertTitle>
            <AlertDescription>
                {{ __('alerts.subscription_past_due_description') }}
                <Link
                    :href="manageUrl"
                    class="ml-2 underline underline-offset-4"
                >
                    {{ __('alerts.manage_subscription') }}
                </Link>
            </AlertDescription>
        </Alert>

        <Alert
            v-else-if="subscriptionAlert?.type === 'expired'"
            variant="destructive"
        >
            <AlertTriangle class="h-4 w-4" />
            <AlertTitle>{{
                __('alerts.subscription_expired_title')
            }}</AlertTitle>
            <AlertDescription>
                {{ __('alerts.subscription_expired_description') }}
                <Link
                    :href="manageUrl"
                    class="ml-2 underline underline-offset-4"
                >
                    {{ __('alerts.renew_now') }}
                </Link>
            </AlertDescription>
        </Alert>

        <Alert
            v-else-if="subscriptionAlert?.type === 'pending'"
            variant="warning"
        >
            <CreditCard class="h-4 w-4" />
            <AlertTitle>
                {{ __('alerts.subscription_pending_title') }}
            </AlertTitle>
            <AlertDescription>
                {{ __('alerts.subscription_pending_description') }}
                <a
                    v-if="subscriptionAlert.payment_url"
                    :href="subscriptionAlert.payment_url"
                    class="ml-2 underline underline-offset-4"
                >
                    {{ __('alerts.resume_payment') }}
                </a>
            </AlertDescription>
        </Alert>

        <!-- Canal WhatsApp -->
        <Alert
            v-for="alert in channelAlerts"
            :key="`${alert.type}-${alert.instance_id}`"
            :variant="alert.type === 'ban' ? 'destructive' : 'warning'"
        >
            <ShieldAlert class="h-4 w-4" />
            <AlertTitle>
                {{
                    alert.type === 'ban'
                        ? __('alerts.channel_banned_title', {
                              instance: alert.instance_name,
                          })
                        : __('alerts.channel_near_limit_title', {
                              instance: alert.instance_name,
                          })
                }}
            </AlertTitle>
            <AlertDescription>
                <template v-if="alert.type === 'ban'">
                    {{
                        __('alerts.channel_banned_description', {
                            date: formatDate(alert.banned_until) ?? '',
                        })
                    }}
                </template>
                <template v-else>
                    {{
                        __('alerts.channel_near_limit_description', {
                            pct: alert.pct ?? 0,
                            sent: alert.sent ?? 0,
                            limit: alert.limit ?? 0,
                        })
                    }}
                </template>
                <Link
                    :href="`/admin/whatsapp-instances/${alert.instance_id}`"
                    class="ml-2 underline underline-offset-4"
                >
                    {{ __('alerts.view_instance') }}
                </Link>
            </AlertDescription>
        </Alert>
    </div>
</template>
